import clsx from 'clsx';
import type * as React from 'react';

import { conflicts } from '@gitbook/doc-core';
import { Badge } from '@gitbook/react-uikit';
import type { Block } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import { BlockLayout } from '../../BlockLayout';
import styles from './BlockConflicts.module.css';

/**
 * Wrapper arround a node to display the block conflict toolbar.
 */
export function BlockConflictsWrapper(props: {
    node: Block;
    editor: EditorContainer;
    children: React.ReactElement;
}): React.ReactElement {
    const { node, editor, children } = props;

    const hasConflicts = conflicts.isConflictKey(node.key);

    if (!hasConflicts) {
        return children;
    }

    const hasConflictsBase = conflicts.hasBlockConflictFrom(node, 'base');
    const hasConflictsHead = conflicts.hasBlockConflictFrom(node, 'head');

    if (!hasConflictsHead && !hasConflictsBase) {
        return children;
    }

    return (
        <div className={clsx('flex flex-col', styles.conflicts)}>
            {hasConflictsBase ? (
                <BlockLayout renderMode="centered">
                    <BlockConflictsToolbar
                        node={node}
                        editor={editor}
                        className={clsx('mt-2 mb-4', styles.conflictBase)}
                    />
                </BlockLayout>
            ) : null}
            <div className={styles.highlightContainer}>
                <div
                    className={clsx(styles.highlight, {
                        [styles.main]: hasConflictsBase,
                        [styles.current]: hasConflictsHead,
                    })}
                />
                {hasConflictsBase ? <div className={styles.verticalConnector} /> : null}
                {children}
                {hasConflictsHead ? (
                    <div className={styles.verticalConnector}>
                        <div className={styles.conflictBadge}>
                            <Badge kind="secondary" icon={Icon.Conflict} />
                        </div>
                    </div>
                ) : null}
            </div>
            {hasConflictsHead ? (
                <BlockLayout renderMode="centered">
                    <BlockConflictsToolbar
                        node={node}
                        editor={editor}
                        className={clsx('mt-2 mb-4', styles.conflictHead)}
                    />
                </BlockLayout>
            ) : null}
        </div>
    );
}

/**
 * Toolbar to pick wished changes for a block with conflicts.
 */
function BlockConflictsToolbar(props: {
    node: Block;
    editor: EditorContainer;
    className?: string;
}): React.ReactElement {
    const { node, editor, className } = props;

    const onPick = () => {
        editor.change((change) => {
            conflicts.pickConflict(change, node.key);
        });
    };

    return (
        <div contentEditable={false} className={clsx('flex', styles.toolbarConnector, className)}>
            {conflicts.isConflictKeyFrom(node.key, 'base') ? (
                <Button
                    leadingIcon={<Icon.Check className="text-merge" />}
                    rounded
                    size="small"
                    kind="secondary"
                    className="mr-4"
                    tooltip={{
                        label: 'Pick recent edits made on the main content',
                        position: 'top',
                    }}
                    onPress={onPick}
                    label="Keep primary"
                />
            ) : (
                <Button
                    leadingIcon={<Icon.Check className="text-primary" />}
                    rounded
                    size="small"
                    kind="secondary"
                    tooltip={{
                        label: 'Pick edits made on this change request',
                        position: 'bottom',
                    }}
                    onPress={onPick}
                    label="Keep changed"
                />
            )}
        </div>
    );
}
